import type {
  ContratoParcela,
  ContratoStatus,
  ContratoTipo,
  ParcelaCondicao,
  ParcelaStatus,
} from '@services/db.js'
import type { BadgeVariant } from '@components/primitives/Badge.js'
import { CONTRATO_TIPOS } from '@utils/constants.js'

export const TIPO_LABEL: Record<ContratoTipo, string> = Object.fromEntries(
  CONTRATO_TIPOS.map((t) => [t.value, t.label]),
) as Record<ContratoTipo, string>

export const STATUS_LABEL: Record<ContratoStatus, string> = {
  ativo: 'Em andamento',
  concluido: 'Concluído',
  cancelado: 'Cancelado',
}

export const STATUS_VARIANT: Record<ContratoStatus, BadgeVariant> = {
  ativo: 'warning',
  concluido: 'success',
  cancelado: 'default',
}

export const CONDICAO_LABEL: Record<ParcelaCondicao, string> = {
  sinal: 'Sinal',
  medicao: 'Medição',
  data: 'Data fixa',
  retencao: 'Retenção',
  aditivo: 'Aditivo',
}

export const PARCELA_STATUS_LABEL: Record<ParcelaStatus, string> = {
  prevista: 'Prevista',
  liberada: 'Liberada',
  paga: 'Paga',
}

export const PARCELA_STATUS_VARIANT: Record<ParcelaStatus, BadgeVariant> = {
  prevista: 'default',
  liberada: 'info',
  paga: 'success',
}

/** Itens sugeridos para a vistoria antes de liberar cada tipo de parcela. */
export const MEDICAO_ITENS_PADRAO: Record<ParcelaCondicao, string[]> = {
  medicao: [
    'Serviço concluído conforme o escopo combinado',
    'Qualidade conferida no local: nível, prumo, esquadro, acabamento',
    'Sem retrabalho ou remendo pendente',
    'Área limpa, entulho retirado',
  ],
  aditivo: [
    'Serviço extra executado como combinado',
    'Valor e escopo do aditivo anotados por escrito',
    'Qualidade conferida no local',
  ],
  data: [
    'Serviço correspondente à parcela está pronto',
    'Qualidade conferida no local',
    'Sem pendências que travem a próxima etapa',
  ],
  retencao: [
    'Vistoria final feita cômodo a cômodo',
    'Todos os retoques e correções executados',
    'Instalações testadas: água, esgoto, elétrica',
    'Garantia, notas e contatos de assistência recebidos',
  ],
  sinal: ['Escopo e parcelas combinados por escrito', 'Data de início confirmada com o prestador'],
}

/** Texto pronto para mandar no WhatsApp do prestador, conforme a próxima parcela. */
export function mensagemWhatsAppParcela(opts: {
  prestador: string
  titulo: string
  parcela: ContratoParcela
  valorFmt: string
}): string {
  const primeiro = opts.prestador.trim().split(/\s+/)[0] ?? opts.prestador
  if (opts.parcela.status === 'liberada') {
    return `Oi ${primeiro}. Conferi "${opts.parcela.descricao}" do contrato ${opts.titulo} (${opts.valorFmt}). Vou pagar. Combinado?`
  }
  return `Oi ${primeiro}. Vou passar no canteiro para medir "${opts.parcela.descricao}" (${opts.valorFmt}) antes de pagar.`
}

export const CONDICOES: { value: ParcelaCondicao; label: string; hint: string }[] = [
  { value: 'medicao', label: 'Medição', hint: 'Paga após o serviço ser medido e aceito.' },
  { value: 'sinal', label: 'Sinal', hint: 'Mobilização. Recomendado até 15%.' },
  { value: 'data', label: 'Data fixa', hint: 'Evite: paga por calendário, não por entrega.' },
  { value: 'retencao', label: 'Retenção', hint: 'Só após vistoria final sem pendências.' },
  { value: 'aditivo', label: 'Aditivo', hint: 'Serviço extra fora do combinado. Documente.' },
]
